import type { FC } from 'react';
import { memo } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { FacetBadges } from '../../../../../front/src/thread/Category/FacetBadges';
import { TopicSubject } from '../../../../../front/src/thread/Topic/TopicSubject';
import type { Topic } from '../../../../../front/src/types/api';
import type { PressArticlesBlockData } from '../../../../../front/src/types/layout';
import {
  getHeroSrc,
  HeroImage,
  pressMessages,
  SectionHead,
  StretchedTitleLink,
} from './pressShared';

interface Props {
  block: PressArticlesBlockData;
  className?: string;
}

const DEFAULT_LIMIT = 8;

const RestItem: FC<{ article: Topic }> = ({ article }) => (
  <div className="relative group flex flex-col py-[18px] border-b border-black/10">
    {/* Rangée vignette + texte */}
    <div className="flex gap-4 items-start">
      {getHeroSrc(article) && (
        <HeroImage
          src={getHeroSrc(article) as string}
          width={112}
          height={112}
          sizes="112px"
          className="flex-none w-28 h-28 rounded object-cover bg-gray-100"
        />
      )}
      <div className="flex-1 min-w-0">
        <h3 className="text-[18.5px] leading-[1.25] font-bold mb-1.5 group-hover:text-primary-base">
          <StretchedTitleLink slug={article.slug}>
            <TopicSubject subject={article.subject as string} />
          </StretchedTitleLink>
        </h3>
        <div className="flex items-center gap-3 text-[13.5px] font-semibold text-gray-medium">
          {article.createdAt && (
            <FormattedDate
              value={new Date(article.createdAt)}
              day="2-digit"
              month="2-digit"
              year="numeric"
            />
          )}
          {typeof article.messagesCount === 'number' && article.messagesCount > 0 && (
            <span className="inline-flex items-center gap-1.5 text-primary-base font-bold">
              <svg viewBox="0 0 24 24" className="w-[15px] h-[15px] fill-current">
                <path d="M7.3 19 4 22V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1z" />
              </svg>
              {article.messagesCount}
            </span>
          )}
        </div>
      </div>
    </div>
    {/* Tags EN PLEINE LARGEUR sous la rangée image+texte : scroll + fondu à droite. */}
    <FacetBadges
      themes={article.themes}
      entities={article.entities}
      disableLink
      plain
      className="relative z-10 mt-1.5 flex w-full min-w-0 flex-nowrap items-center overflow-x-auto whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)] [&::-webkit-scrollbar]:hidden"
    />
  </div>
);

/** "Le reste de l'actu" — two columns of recent editorial articles. Media mode only. */
export const BlockPressRest: FC<Props> = memo(function BlockPressRest({ block }) {
  const limit = block.options?.limit ? parseInt(block.options.limit, 10) : DEFAULT_LIMIT;
  const articles = (block.data || []).slice(0, limit);
  if (!articles.length) return null;

  const mid = Math.ceil(articles.length / 2);
  const columns = [articles.slice(0, mid), articles.slice(mid)];

  return (
    <section className="BlockPressRest max-w-5xl mx-auto px-7 pt-16">
      <SectionHead
        title={block.options?.customTitle || <FormattedMessage {...pressMessages.rest} />}
      />
      <div className="grid gap-x-12 grid-cols-[repeat(auto-fit,minmax(320px,1fr))]">
        {columns.map((col, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="flex flex-col">
            {col.map(article => (
              <RestItem key={article.id} article={article} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
});
